<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="resourceName || 'PPT预览'"
    width="90%"
    top="3vh"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    @close="handleClose"
    class="ppt-preview-dialog"
  >
    <div class="ppt-preview-container" v-loading="loading">
      <!-- 幻灯片显示区域 -->
      <div class="slide-viewer" ref="slideViewer">
        <img
          v-if="currentSlideUrl"
          :src="currentSlideUrl"
          :alt="'第' + (currentSlide + 1) + '页'"
          class="slide-image"
          @contextmenu.prevent
          @dragstart.prevent
        />
        <div v-else class="slide-placeholder">
          <i class="el-icon-picture-outline"></i>
          <p>加载中...</p>
        </div>
      </div>

      <!-- 控制栏 -->
      <div class="control-bar">
        <div class="slide-nav">
          <el-button
            icon="el-icon-arrow-left"
            :disabled="currentSlide === 0"
            @click="prevSlide"
            circle
          ></el-button>

          <span class="slide-info">
            {{ currentSlide + 1 }} / {{ slideCount }}
          </span>

          <el-button
            icon="el-icon-arrow-right"
            :disabled="currentSlide >= slideCount - 1"
            @click="nextSlide"
            circle
          ></el-button>
        </div>

        <!-- 缩略图导航 -->
        <div class="thumbnail-nav" v-if="slideCount > 0">
          <div
            v-for="i in slideCount"
            :key="i - 1"
            :class="['thumbnail-item', { active: currentSlide === i - 1 }]"
            @click="goToSlide(i - 1)"
          >
            {{ i }}
          </div>
        </div>

        <!-- 全屏按钮 -->
        <el-button
          icon="el-icon-full-screen"
          @click="toggleFullscreen"
          circle
          class="fullscreen-btn"
        ></el-button>
      </div>

      <!-- 版权提示 -->
      <div class="copyright-notice">
        <i class="el-icon-warning-outline"></i>
        本课件仅供学习使用，禁止下载传播
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { getPptPreviewInfo, getSlideImageUrl } from '@/api/amcPptPreview'

export default {
  name: 'PptPreviewDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    resourceId: {
      type: [Number, String],
      default: null
    },
    resourceName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      slideCount: 0,
      currentSlide: 0,
      previewInfo: null,
      isFullscreen: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    currentSlideUrl() {
      if (!this.resourceId || this.slideCount === 0) return ''
      // 添加时间戳防止缓存
      return getSlideImageUrl(this.resourceId, this.currentSlide) + '?t=' + Date.now()
    }
  },
  watch: {
    visible(val) {
      if (val && this.resourceId) {
        this.loadPreviewInfo()
      }
    },
    resourceId(val) {
      if (val && this.visible) {
        this.loadPreviewInfo()
      }
    }
  },
  methods: {
    async loadPreviewInfo() {
      if (!this.resourceId) return

      this.loading = true
      this.currentSlide = 0
      this.slideCount = 0

      try {
        console.log('加载PPT预览信息, resourceId:', this.resourceId)
        const res = await getPptPreviewInfo(this.resourceId)
        console.log('PPT预览响应:', res)
        if (res.code === 200 || res.code === 1) {
          this.previewInfo = res.data
          this.slideCount = res.data.slideCount || 0
          if (this.slideCount === 0) {
            this.$message.warning('PPT转换中，请稍后重试')
          }
        } else {
          console.error('PPT预览失败:', res.msg)
          this.$message.error(res.msg || '加载PPT预览失败')
        }
      } catch (e) {
        console.error('加载PPT预览失败', e)
        const errMsg = e.response?.data?.msg || e.message || '加载PPT预览失败'
        this.$message.error(errMsg)
      } finally {
        this.loading = false
      }
    },

    prevSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--
      }
    },

    nextSlide() {
      if (this.currentSlide < this.slideCount - 1) {
        this.currentSlide++
      }
    },

    goToSlide(index) {
      if (index >= 0 && index < this.slideCount) {
        this.currentSlide = index
      }
    },

    toggleFullscreen() {
      const container = this.$refs.slideViewer
      if (!container) return

      if (!this.isFullscreen) {
        if (container.requestFullscreen) {
          container.requestFullscreen()
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen()
        } else if (container.msRequestFullscreen) {
          container.msRequestFullscreen()
        }
        this.isFullscreen = true
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
        this.isFullscreen = false
      }
    },

    handleClose() {
      this.currentSlide = 0
      this.slideCount = 0
      this.previewInfo = null
      this.$emit('close')
    },

    // 键盘导航
    handleKeydown(e) {
      if (!this.visible) return

      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        this.prevSlide()
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        this.nextSlide()
      } else if (e.key === 'Home') {
        this.goToSlide(0)
      } else if (e.key === 'End') {
        this.goToSlide(this.slideCount - 1)
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)

    // 监听全屏变化
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement
    })
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
  }
}
</script>

<style scoped>
.ppt-preview-dialog ::v-deep .el-dialog__body {
  padding: 10px;
  background: #1a1a1a;
}

.ppt-preview-container {
  display: flex;
  flex-direction: column;
  height: 75vh;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
}

.slide-viewer {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2d2d2d;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.slide-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}

.slide-placeholder {
  color: #666;
  text-align: center;
}

.slide-placeholder i {
  font-size: 64px;
  margin-bottom: 16px;
}

.control-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
  background: #252525;
  border-radius: 0 0 8px 8px;
  gap: 20px;
}

.slide-nav {
  display: flex;
  align-items: center;
  gap: 15px;
}

.slide-info {
  color: #fff;
  font-size: 16px;
  min-width: 80px;
  text-align: center;
}

.thumbnail-nav {
  display: flex;
  gap: 8px;
  max-width: 400px;
  overflow-x: auto;
  padding: 5px;
}

.thumbnail-item {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3a3a3a;
  color: #aaa;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.thumbnail-item:hover {
  background: #4a4a4a;
  color: #fff;
}

.thumbnail-item.active {
  background: #409eff;
  color: #fff;
}

.fullscreen-btn {
  margin-left: auto;
}

.copyright-notice {
  position: absolute;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: #ff9800;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 禁用右键菜单和选择 */
.ppt-preview-container {
  user-select: none;
  -webkit-user-select: none;
}

/* 全屏模式样式 */
.slide-viewer:fullscreen {
  background: #fff;
  padding: 0;
}

.slide-viewer:fullscreen .slide-image {
  width: 100vw;
  height: 100vh;
  max-width: none;
  max-height: none;
  object-fit: contain;
  background: #fff;
}

.slide-viewer:-webkit-full-screen {
  background: #fff;
  padding: 0;
}

.slide-viewer:-webkit-full-screen .slide-image {
  width: 100vw;
  height: 100vh;
  max-width: none;
  max-height: none;
  object-fit: contain;
  background: #fff;
}

/* 响应式 */
@media (max-width: 768px) {
  .thumbnail-nav {
    display: none;
  }

  .control-bar {
    padding: 10px;
  }
}
</style>
